import { OptionsDeleteIcon } from 'assets/icons/OptionsDeleteIcon'
import { Badge } from 'components/Badge/Badge'
import { Container, ContainerHeader, ContainerContent } from 'components/Containers/Containers'
import { HonestSelectInput } from 'components/HonestSelect/HonestSelectInput'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { Cell, RightCell } from 'components/HonestTable/HonestTable.styled'
import { Input } from 'components/Input/Input'
import { AddPhaseSection, NotYetAdded, SandboxPreview } from 'pages/Operations/Operations.styled'
import { useState } from 'react'
import { Button, CursorPointer, Flex } from 'styling/GlobalStyles'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import { CloseIcon } from 'assets/icons/CloseIcon'
import { IAddOperationPlanTab, IPhaseState } from 'types/operationPlans.interface'
import { ISelectValue } from 'constants/globalTypes'
import { HonestSelectAsyncInput } from 'components/HonestAsyncSelect/HonestAsyncSelectInput'
import { toast } from 'react-toastify'
import { advanceRuleList, executableTypeList } from '../../plansData'

export const Phases = ({ setStep, setState, state }: IAddOperationPlanTab) => {
    const [showAddPhase, setShowAddPhase] = useState(false)
    const [addingPhase, setAddingPhase] = useState<IPhaseState>({
        id: null,
        name: null,
        sequence: null,
        executableType: null,
        advanceRule: null,
        workflowTemplate: null,
        processTemplate: null,
        taskCycleTemplate: null,
        bindingConfig: null,
    })
    const handleNext = () => setStep(5)
    const handleBack = () => setStep(3)

    const handleDeletePhase = (id: number) => {
        setState((prev) => ({ ...prev, phases: prev.phases.filter((phase) => phase.id !== id) }))
    }

    const columns: IHonestTableColumn<IPhaseState>[] = [
        {
            id: 'name',
            header: 'Name',
            sticky: true,
            minWidth: '14rem',
            cell: (item) => (
                <Cell>
                    <Flex column gap='0.2rem' >
                        <BodyText weight='600'>{item.name}</BodyText>
                        <OtherText grey >
                            {item.sequence?.toString()}
                        </OtherText>
                    </Flex>
                </Cell>
            ),
        },
        {
            id: 'spacer',
            header: '',
            fill: true,
            cell: () => null,
        },
        {
            id: 'type',
            header: 'Type',
            align: 'right',
            cell: (item) => (
                <RightCell>
                    <Badge hexColor='#ff9900'>
                        <BodyText>{item.executableType?.label}</BodyText>
                    </Badge>
                </RightCell>
            ),
        },
        {
            id: 'rule',
            header: 'Rule',
            align: 'right',
            cell: (item) => (
                <RightCell>
                    <BodyText>{item.advanceRule?.label}</BodyText>
                </RightCell>
            ),
        },
        {
            id: 'delete',
            header: '',
            align: 'right',
            minWidth: '3rem',
            cell: (item) => (
                <RightCell>
                    <CursorPointer onClick={() => handleDeletePhase(item.id)} >
                        <OptionsDeleteIcon />
                    </CursorPointer>
                </RightCell>
            ),
        },
    ]

    const handleSelect = (v: ISelectValue, n: string) => {
        setAddingPhase((prev) => {
            if (n === 'executableType') {
                return {
                    ...prev,
                    executableType: v,
                    taskCycleTemplate: null,
                    workflowTemplate: null,
                    processTemplate: null,
                }
            }
            return { ...prev, [n]: v }
        })
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value, name, type } = e.target
        setAddingPhase((prev) => ({ ...prev, [name]: type === 'number' ? Number(value) : value }))
    }

    const handleAddPhase = () => {

        if (!addingPhase.name || !addingPhase.sequence || !addingPhase.executableType || !addingPhase.advanceRule) {
            toast.error('Please fill all required fields')
            return
        }

        const id = Date.now()
        setState((prev) => ({ ...prev, phases: [...prev.phases, { ...addingPhase, id }] }))
        setAddingPhase({
            id,
            name: null,
            sequence: 0,
            executableType: null,
            advanceRule: null,
            processTemplate: null,
            taskCycleTemplate: null,
            bindingConfig: null,
        })
        setShowAddPhase(false)
    }

    const renderExecutableSelect = () => {

        const parse = (data: any) => {
            return data ? data.data.map((item: any) => ({ label: item.name, value: item.id })) : []
        }

        switch (addingPhase.executableType?.value) {
            case 1:
                return <HonestSelectAsyncInput
                    keyProp={1}
                    defaultValue={addingPhase.taskCycleTemplate?.label}
                    link='task/cycle/template'
                    labelText='Task Cycle Template'
                    onChange={(type: ISelectValue, name: string) => handleSelect(type, name)}
                    name='taskCycleTemplate'
                    placeholder='Search Task Cycles'
                    parserFunction={parse}
                    required
                    invalid={!addingPhase.taskCycleTemplate?.value}
                />

            case 2:
                return <HonestSelectAsyncInput
                    keyProp={2}
                    defaultValue={addingPhase.workflowTemplate?.label}
                    link='workflow/template'
                    labelText='Workflow Template'
                    onChange={(type: ISelectValue, name: string) => handleSelect(type, name)}
                    name='workflowTemplate'
                    placeholder='Search Workflow Templates'
                    parserFunction={parse}
                    required
                    invalid={!addingPhase.workflowTemplate?.value}
                />

            case 3:
                return <HonestSelectAsyncInput
                    keyProp={3}
                    defaultValue={addingPhase.processTemplate?.label}
                    link='process/template/company'
                    labelText='Process Template'
                    onChange={(type: ISelectValue, name: string) => handleSelect(type, name)}
                    name='processTemplate'
                    placeholder='Search Process Templates'
                    parserFunction={parse}
                    required
                    invalid={!addingPhase.processTemplate?.value}
                />

        }
    }

    return <>
        <Container>
            <ContainerHeader>
                <TitleText>Phases</TitleText>
            </ContainerHeader>
            <ContainerContent>
                {state.phases.length > 0 ? <HonestTable
                    columns={columns}
                    data={state.phases}
                    keyField='id'
                /> : <NotYetAdded><BodyText>No phases added yet</BodyText></NotYetAdded>}
                {!showAddPhase ? <Button mt='16' orangeBorder onClick={() => setShowAddPhase(true)} >Add Phase</Button> : <AddPhaseSection>
                    <Flex between width='100%' mb='16' >
                        <TitleText weight='bold' >Add Phase</TitleText>
                        <CloseIcon onClick={() => setShowAddPhase(false)} />
                    </Flex>
                    <Flex gap='1rem' mb='16'>
                        <Input placeholder='Enter phase name...' labelText='Name' name='name' value={addingPhase.name} onChange={handleChange} nomarg required invalid={!addingPhase.name} />
                        <Input placeholder='Enter sequence' labelText='Sequence' name='sequence' value={addingPhase.sequence} onChange={handleChange} nomarg required invalid={!addingPhase.sequence} type='number' />
                        <HonestSelectInput
                            options={executableTypeList}
                            labelText='Executable type'
                            name='executableType'
                            onChange={handleSelect}
                            defaultValue={addingPhase.executableType}
                            invalid={!addingPhase.executableType?.value}
                            required
                        />
                        <HonestSelectInput
                            options={advanceRuleList}
                            labelText='Advance rule'
                            name='advanceRule'
                            onChange={handleSelect}
                            defaultValue={addingPhase.advanceRule}
                            invalid={!addingPhase.advanceRule?.value}
                            required
                        />
                    </Flex>
                    {renderExecutableSelect()}
                    <Flex gap='1rem' mt='16' width='100%' justend>
                        <Button orange onClick={handleAddPhase} >Add</Button>
                    </Flex>

                </AddPhaseSection>}
                <SandboxPreview>
                    <BodyText weight='bold' >Sandbox Preview</BodyText>
                    <Flex width='100%' mt='16'>
                        <Flex column gap='0.2rem' width='100%' >
                            <BodyText grey >CYCLE</BodyText>
                            <BodyText  >{state.phases.filter((phase) => phase.executableType?.value === 1).length}</BodyText>
                        </Flex>
                        <Flex column gap='0.2rem' width='100%' >
                            <BodyText grey >WORKFLOW</BodyText>
                            <BodyText  >{state.phases.filter((phase) => phase.executableType?.value === 2).length}</BodyText>
                        </Flex>
                        <Flex column gap='0.2rem' width='100%' >
                            <BodyText grey >PROCESS</BodyText>
                            <BodyText  >{state.phases.filter((phase) => phase.executableType?.value === 3).length}</BodyText>
                        </Flex>
                        <Flex column gap='0.2rem' width='100%' >
                            <BodyText grey >AUTO</BodyText>
                            <BodyText  >{state.phases.filter((phase) => phase.advanceRule?.value === 1).length}</BodyText>
                        </Flex>
                        <Flex column gap='0.2rem' width='100%' >
                            <BodyText grey >MANUAL GATE</BodyText>
                            <BodyText  >{state.phases.filter((phase) => phase.advanceRule?.value === 2).length}</BodyText>
                        </Flex>
                    </Flex>
                </SandboxPreview>

            </ContainerContent>

        </Container>
        <Flex gap='1rem' mt='32' justend>
            <Button onClick={handleBack}>Back</Button>
            <Button orange onClick={handleNext} >Next</Button>
        </Flex>
    </>
}

